import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore'; 
import { auth, db } from '../services/firebase';

const AuthContext = createContext({
  currentUser: null,
  userData: null, 
  loading: true,
  // RBAC Role Flags (Apex OS Taxonomy)
  isFounder: false,
  isClientOwner: false,
  isAdmin: false,
  isRegionalDirector: false,
  isStoreManager: false,
  isAssociate: false,
  // PBAC Evaluation Engine
  hasPermission: () => false
});

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Role States
  const [isFounder, setIsFounder] = useState(false);
  const [isClientOwner, setIsClientOwner] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isRegionalDirector, setIsRegionalDirector] = useState(false);
  const [isStoreManager, setIsStoreManager] = useState(false);
  const [isAssociate, setIsAssociate] = useState(false);

  // --- PBAC EVALUATION ENGINE (Apex OS Parity) ---
  const hasPermission = useCallback((requiredPermission) => {
    if (!userData) return false;

    // 1. Master Overrides (God Mode)
    const role = (userData.role || '').toUpperCase();
    if (
      role === 'CLIENT_OWNER' || 
      role === 'SYS_FOUNDER' || 
      role === 'FOUNDER' || 
      (userData.permissions && userData.permissions.includes('admin:all'))
    ) {
      return true;
    }

    // 2. Explicit Revocation Check
    if (userData.revokedPermissions && userData.revokedPermissions.includes(requiredPermission)) {
      return false; 
    }

    // 3. Granted / Custom Check
    const basePermissions = userData.permissions || [];
    const customPermissions = userData.customPermissions || [];

    return basePermissions.includes(requiredPermission) || customPermissions.includes(requiredPermission);
  }, [userData]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setCurrentUser(user);
        
        try {
          // REMOVED 'true' TO UTILIZE CACHE AND ELIMINATE NETWORK LATENCY
          const idTokenResult = await user.getIdTokenResult();
          
          let roleClaim = (idTokenResult.claims.role || '').toUpperCase();
          let hasFounderAccess = !!(
            idTokenResult.claims.SYS_FOUNDER || 
            idTokenResult.claims.FOUNDER || 
            idTokenResult.claims.sys_founder || 
            idTokenResult.claims.founder
          );

          try {
            // Enterprise Fix: Direct UID lookup satisfies your strict Firebase Security Rules
            const userDocRef = doc(db, 'users', user.uid);
            const userDocSnap = await getDoc(userDocRef);

            if (userDocSnap.exists()) {
              const profileData = userDocSnap.data();
              setUserData(profileData);
              
              // Standardize the role to uppercase for bulletproof matching
              const dbRole = (profileData.role || '').toUpperCase();
              const activeRole = dbRole || roleClaim; // DB overrides token claims
              const rolesArray = (profileData.roles || []).map(r => r.toUpperCase());

              if (
                activeRole === 'SYS_FOUNDER' || 
                activeRole === 'FOUNDER' || 
                rolesArray.includes('SYS_FOUNDER') || 
                rolesArray.includes('FOUNDER')
              ) {
                hasFounderAccess = true;
              }

              // Set Apex OS Taxonomy Roles
              setIsClientOwner(activeRole === 'CLIENT_OWNER' || activeRole === 'MASTER_AGENT');
              setIsAdmin(activeRole === 'ADMIN');
              setIsRegionalDirector(activeRole === 'REGIONAL_DIRECTOR');
              setIsStoreManager(activeRole === 'STORE_MANAGER');
              setIsAssociate(activeRole === 'ASSOCIATE' || activeRole === 'REP');
            }
          } catch (firestoreError) {
            console.warn("Firestore scan blocked by security rules, relying on master token.", firestoreError);
            
            // Fallback to token claims if DB read fails
            setIsClientOwner(roleClaim === 'CLIENT_OWNER' || roleClaim === 'MASTER_AGENT');
            setIsAdmin(roleClaim === 'ADMIN');
            setIsRegionalDirector(roleClaim === 'REGIONAL_DIRECTOR');
            setIsStoreManager(roleClaim === 'STORE_MANAGER');
            setIsAssociate(roleClaim === 'ASSOCIATE' || roleClaim === 'REP');
          }

          setIsFounder(hasFounderAccess);
        } catch (error) {
          console.error("Auth initialization error:", error);
          resetRoles();
        }
      } else {
        setCurrentUser(null);
        setUserData(null);
        resetRoles();
      }
      
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const resetRoles = () => {
    setIsFounder(false);
    setIsClientOwner(false);
    setIsAdmin(false);
    setIsRegionalDirector(false);
    setIsStoreManager(false);
    setIsAssociate(false);
  };

  return (
    <AuthContext.Provider value={{ 
      currentUser, 
      userData, 
      loading,
      isFounder,
      isClientOwner,
      isAdmin,
      isRegionalDirector,
      isStoreManager,
      isAssociate,
      hasPermission
    }}>
      {!loading && children}
    </AuthContext.Provider>
  );
}

export default AuthProvider;